import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Buildings,
  ChatDots,
  ChatLeftText,
  CheckCircleFill,
  Link45deg,
  PencilSquare,
  PlusLg,
  Stars,
  XLg,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { useAuth } from '../auth/AuthContext';
import {
  BRAND_BUILDING_PRICE_FALLBACK,
  REQUEST_DESCRIPTION_MAX,
  REQUEST_LINKS_MAX,
  createBuildingRequest,
  existingThreadFromError,
  fetchBrandBuildingInfo,
  fetchBrandBuildings,
  formatHuf,
} from '../api/brandBuildings';
import BuildingViewer from '../components/building/BuildingViewer';
import { SUPPORT_PATH } from '../components/building/BrandBuildingSection';
import '../components/building/BrandBuilding.scss';
import './BrandBuildingPage.scss';

const PAGE_SIZE = 24;
const DESCRIPTION_MAX = REQUEST_DESCRIPTION_MAX;
const MAX_LINKS = REQUEST_LINKS_MAX;

// "Custom brand building" landing, inside the dashboard shell
// (/dashboard/brand-building). Hero + price, the process, the gallery of every
// live custom building, and the request form — which opens a support chat
// (topic brand_building) and takes the partner straight into it.
export default function BrandBuildingPage({ brand }) {
  const { t, lang } = useLocale();
  const location = useLocation();
  const redesign = new URLSearchParams(location.search).get('redesign') === '1';
  const [info, setInfo] = useState(null);
  const [galleryPrice, setGalleryPrice] = useState(null);
  // A few real buildings for the hero, taken from the gallery's first page.
  const [featured, setFeatured] = useState([]);
  const onFirstPage = useCallback((items) => {
    setFeatured(items.filter((b) => b.thumbnailUrl).slice(0, 3));
  }, []);
  const formRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchBrandBuildingInfo(brand.id)
      .then((res) => alive && setInfo(res))
      .catch(() => alive && setInfo({ building: null, priceFromHuf: null, openThread: null }));
    return () => {
      alive = false;
    };
  }, [brand.id]);

  // Arriving from the datasheet's "Redesign" lands on the form.
  useEffect(() => {
    if (location.hash === '#request' && formRef.current) {
      formRef.current.scrollIntoView({ block: 'start' });
    }
  }, [location.hash]);

  const price = formatHuf(info?.priceFromHuf || galleryPrice || BRAND_BUILDING_PRICE_FALLBACK, lang);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="bbp">
      <Link className="bbp__back" to="/dashboard/profile">
        <ArrowLeft size={14} /> {t('building.backToProfile')}
      </Link>

      <header className="bbp-hero">
        <div className="bbp-hero__dots" aria-hidden="true" />
        <div className="bbp-hero__body">
          <span className="bbp-hero__eyebrow">{t('building.eyebrow')}</span>
          <h1 className="bbp-hero__title">{t('building.heroTitle')}</h1>
          <p className="bbp-hero__lead">{t('building.heroLead')}</p>
          <div className="bbp-hero__price">
            <span className="bbp-hero__price-label">{t('building.priceLabel')}</span>
            <strong>{t('building.priceFrom', { price })}</strong>
          </div>
          <div className="bbp-hero__actions">
            <button type="button" className="btn-o btn-o--light" onClick={() => scrollTo('request')}>
              {redesign ? t('building.heroCtaRedesign') : t('building.heroCta')}
              <ArrowRight size={16} />
            </button>
            <button type="button" className="btn-o bbp-hero__ghost" onClick={() => scrollTo('gallery')}>
              {t('building.heroGallery')}
            </button>
          </div>
        </div>
        {featured.length > 0 && (
          <div className={`bbp-hero__art bbp-hero__art--${featured.length}`} aria-hidden="true">
            {featured.map((b) => (
              <figure key={b.brandId} className="bbp-hero__card">
                <img src={b.thumbnailUrl} alt="" decoding="async" />
                <figcaption>{b.brandName}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </header>

      <section className="bbp-steps" aria-labelledby="bbp-steps-title">
        <h2 className="bbp__h2" id="bbp-steps-title">{t('building.processTitle')}</h2>
        <p className="bbp__lead">{t('building.processLead')}</p>
        <ol className="bbp-steps__list">
          {[
            { Icon: ChatLeftText, k: 1 },
            { Icon: Stars, k: 2 },
            { Icon: PencilSquare, k: 3 },
            { Icon: Buildings, k: 4 },
          ].map(({ Icon, k }) => (
            <li key={k} className="bbp-step">
              <span className="bbp-step__icon"><Icon size={18} /></span>
              <span className="bbp-step__num">{k}</span>
              <strong className="bbp-step__title">{t(`building.step${k}Title`)}</strong>
              <span className="bbp-step__body">{t(`building.step${k}Body`)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="bbp-request" id="request" ref={formRef} aria-labelledby="bbp-request-title">
        <h2 className="bbp__h2" id="bbp-request-title">
          {redesign ? t('building.formTitleRedesign') : t('building.formTitle')}
        </h2>
        <p className="bbp__lead">{t('building.formLead', { price })}</p>
        {info?.openThread ? (
          <OpenThreadCard thread={info.openThread} />
        ) : (
          <RequestForm brand={brand} redesign={redesign} />
        )}
      </section>

      {/* Last on the page: it grows as it scrolls (infinite paging), so
          nothing may sit below it. */}
      <Gallery onPrice={setGalleryPrice} onFirstPage={onFirstPage} />
    </div>
  );
}

function OpenThreadCard({ thread }) {
  const { t } = useLocale();
  return (
    <div className="panel-card bbp-open">
      <span className="bbp-open__icon"><ChatDots size={20} /></span>
      <div className="bbp-open__text">
        <strong>{t('building.openThreadTitle')}</strong>
        <span>{t('building.openThreadLanding')}</span>
      </div>
      <Link className="btn-o btn-o--dark btn-o--sm" to={`${SUPPORT_PATH}/${thread.id}`}>
        {t('building.openThreadCta')}
      </Link>
    </div>
  );
}

// ── Request form ─────────────────────────────────────────────────────────────

function RequestForm({ brand, redesign }) {
  const { t } = useLocale();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [links, setLinks] = useState(['']);
  const [email, setEmail] = useState(user?.email || '');
  const [state, setState] = useState('idle'); // idle | sending | error
  const [existing, setExisting] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
    // Prefill once the session arrives; never overwrite what was typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const descOk = description.trim().length >= 10;

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    if (!descOk || state === 'sending') return;
    setState('sending');
    // A redesign is said up front, so the team reading the chat knows it is
    // not a first building. The API writes the chat's first message from
    // these fields (description, then links, then the contact email).
    const prefix = redesign ? t('building.redesignPrefix') : '';
    try {
      const thread = await createBuildingRequest(brand.id, {
        description: `${prefix}${description.trim()}`.slice(0, DESCRIPTION_MAX),
        referenceLinks: links,
        contactEmail: email,
      });
      navigate(thread?.id ? `${SUPPORT_PATH}/${thread.id}` : SUPPORT_PATH, { state: { justCreated: true } });
    } catch (err) {
      if (err?.status === 409) {
        const th = existingThreadFromError(err);
        if (th) {
          navigate(`${SUPPORT_PATH}/${th.id}`, { state: { alreadyOpen: true } });
          return;
        }
        setExisting(true);
        setState('idle');
        return;
      }
      setState(err?.status === 400 ? 'invalid' : 'error');
    }
  }

  if (existing) {
    return (
      <div className="panel-card bbp-open">
        <span className="bbp-open__icon"><ChatDots size={20} /></span>
        <div className="bbp-open__text">
          <strong>{t('building.openThreadTitle')}</strong>
          <span>{t('building.openThreadLanding')}</span>
        </div>
        <Link className="btn-o btn-o--dark btn-o--sm" to={SUPPORT_PATH}>
          {t('building.openThreadCta')}
        </Link>
      </div>
    );
  }

  return (
    <form className="panel-card bbp-form" onSubmit={submit} noValidate>
      {redesign && (
        <div className="bbp-form__note">
          <PencilSquare size={15} /> {t('building.redesignNote')}
        </div>
      )}
      <label className="field">
        <span>{t('building.fieldDescription')} *</span>
        <textarea
          rows={6}
          value={description}
          maxLength={DESCRIPTION_MAX}
          placeholder={t('building.fieldDescriptionHint')}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => setTouched(true)}
          aria-invalid={touched && !descOk}
          required
        />
        <small className={`bbp-form__meta${touched && !descOk ? ' is-error' : ''}`}>
          {touched && !descOk ? t('building.fieldDescriptionError') : ' '}
          <span>{description.length} / {DESCRIPTION_MAX}</span>
        </small>
      </label>

      <div className="field">
        <span>{t('building.fieldLinks')}</span>
        <div className="bbp-links">
          {links.map((l, i) => (
            <div className="bbp-links__row" key={i}>
              <Link45deg className="bbp-links__icon" size={16} />
              <input
                type="url"
                inputMode="url"
                value={l}
                placeholder="https://"
                aria-label={`${t('building.fieldLinks')} ${i + 1}`}
                onChange={(e) => setLinks((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))}
              />
              {links.length > 1 && (
                <button
                  type="button"
                  className="bbp-links__remove"
                  aria-label={t('building.linkRemove')}
                  onClick={() => setLinks((arr) => arr.filter((_, j) => j !== i))}
                >
                  <XLg size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
        {links.length < MAX_LINKS && (
          <button type="button" className="bbp-links__add" onClick={() => setLinks((arr) => [...arr, ''])}>
            <PlusLg size={13} /> {t('building.linkAdd')}
          </button>
        )}
        <small className="bbp-form__hint">{t('building.fieldLinksHint')}</small>
      </div>

      <label className="field">
        <span>{t('building.fieldEmail')}</span>
        <input type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
      </label>

      {(state === 'error' || state === 'invalid') && (
        <div className="bbp-form__error" role="alert">
          {t(state === 'invalid' ? 'building.sendInvalid' : 'building.sendError')}
        </div>
      )}

      <div className="bbp-form__foot">
        <button type="submit" className="btn-o btn-o--brand" disabled={state === 'sending'}>
          {state === 'sending' ? t('building.sending') : redesign ? t('building.submitRedesign') : t('building.submit')}
        </button>
        <span className="bbp-form__fine">{t('building.submitFine')}</span>
      </div>
    </form>
  );
}

// ── Gallery ──────────────────────────────────────────────────────────────────
// Scales to hundreds of buildings: thumbnails only (lazy images, cheap
// placeholders), cursor-paged on scroll, and exactly one live WebGL viewer —
// the modal — which is disposed when it closes.

function Gallery({ onPrice, onFirstPage }) {
  const { t } = useLocale();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [total, setTotal] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [open, setOpen] = useState(null);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const cursorRef = useRef(null);
  cursorRef.current = cursor;

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setStatus('loading');
    try {
      const page = await fetchBrandBuildings({ cursor: cursorRef.current, limit: PAGE_SIZE });
      setItems((prev) => {
        const seen = new Set(prev.map((b) => b.brandId));
        return [...prev, ...page.items.filter((b) => !seen.has(b.brandId))];
      });
      setCursor(page.nextCursor);
      setDone(!page.nextCursor || page.items.length === 0);
      if (page.total != null) setTotal(page.total);
      if (page.priceFromHuf) onPrice?.(page.priceFromHuf);
      if (!cursorRef.current) onFirstPage?.(page.items);
      setStatus('idle');
    } catch {
      setStatus('error');
    } finally {
      loadingRef.current = false;
    }
  }, [onPrice, onFirstPage]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  // Infinite scroll: fetch the next page well before the end comes into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || done || status === 'error' || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '800px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [done, status, loadMore, items.length]);

  const empty = done && items.length === 0 && status !== 'error';
  const count = total ?? (done ? items.length : null);

  return (
    <section className="bbp-gallery" id="gallery" aria-labelledby="bbp-gallery-title">
      <div className="bbp-gallery__head">
        <h2 className="bbp__h2" id="bbp-gallery-title">
          {t('building.galleryTitle')}
          {count != null && count > 0 && <span className="product-count">{count}</span>}
        </h2>
        <p className="bbp__lead">{t('building.galleryLead')}</p>
      </div>

      {empty ? (
        <div className="bbp-gallery__empty">
          <span className="bbp-gallery__empty-icon"><Buildings size={26} /></span>
          <strong>{t('building.galleryEmptyTitle')}</strong>
          <span>{t('building.galleryEmptyBody')}</span>
        </div>
      ) : (
        <ul className="bbp-grid">
          {items.map((b) => (
            <li key={b.brandId} className="bbp-card">
              <button type="button" className="bbp-card__btn" onClick={() => setOpen(b)} aria-label={t('building.galleryOpen', { name: b.brandName })}>
                <span className="bbp-card__media">
                  {b.thumbnailUrl ? (
                    <img src={b.thumbnailUrl} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span className="bbp-card__ph">
                      {b.brandLogoUrl ? (
                        <img src={b.brandLogoUrl} alt="" loading="lazy" decoding="async" />
                      ) : (
                        <span>{(b.brandName || '?')[0].toUpperCase()}</span>
                      )}
                    </span>
                  )}
                  <span className="bbp-card__play"><Buildings size={13} /> 3D</span>
                </span>
                <span className="bbp-card__name">
                  {b.brandLogoUrl && <img className="bbp-card__logo" src={b.brandLogoUrl} alt="" loading="lazy" decoding="async" />}
                  <span>{b.brandName}</span>
                </span>
              </button>
            </li>
          ))}
          {status === 'loading' &&
            Array.from({ length: items.length ? 4 : 8 }, (_, i) => (
              <li key={`sk${i}`} className="bbp-card bbp-card--skeleton" aria-hidden="true">
                <span className="bbp-card__media" />
                <span className="bbp-card__name"><span /></span>
              </li>
            ))}
        </ul>
      )}

      {status === 'error' && (
        <div className="bbp-gallery__error">
          <span>{t('building.galleryError')}</span>
          <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={loadMore}>
            {t('building.retry')}
          </button>
        </div>
      )}
      {!done && status !== 'error' && <div ref={sentinelRef} className="bbp-gallery__sentinel" aria-hidden="true" />}

      {open && <GalleryModal building={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function GalleryModal({ building, onClose }) {
  const { t } = useLocale();
  const closeRef = useRef(null);
  const modelUrl = useMemo(() => building.lod0Url, [building]);

  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('bbp-modal-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('bbp-modal-open');
      prev?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div className="bbp-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bbp-modal__panel" role="dialog" aria-modal="true" aria-labelledby="bbp-modal-title">
        <div className="bbp-modal__head">
          <span className="bbp-modal__id">
            {building.brandLogoUrl && <img src={building.brandLogoUrl} alt="" />}
            <strong id="bbp-modal-title">{building.brandName}</strong>
            <span className="bb-badge bb-badge--custom"><CheckCircleFill size={11} /> {t('building.badgeCustom')}</span>
          </span>
          <button type="button" className="bbp-modal__close" ref={closeRef} onClick={onClose} aria-label={t('building.close')}>
            <XLg size={14} />
          </button>
        </div>
        <BuildingViewer
          className="bbp-modal__viewer"
          modelUrl={modelUrl}
          logoUrl={building.brandLogoUrl}
          brandName={building.brandName}
          fallbackImage={building.thumbnailUrl}
          zoom
        />
      </div>
    </div>,
    document.body
  );
}
